import * as React from 'react'
import { CircleDollarSign } from 'lucide-react'
import type { BillingPriceDto } from '@lib/dto/billing.dto'
import { formatPrice } from '@lib/helpers/money'
import { useAuth } from '@/auth/AuthContext'
import { SchedulePriceDialog } from '@/components/billing/SchedulePriceDialog'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { usePriceSchedule } from '@/hooks/useOpsPricing'

/** Increase or decrease against the price before it; the launch price is neither. */
function ChangeBadge({ price }: { price: BillingPriceDto }) {
  if (price.isIncrease === null) return <span className="text-muted-foreground">Launch price</span>
  return price.isIncrease ? <Badge tone="warning">Increase</Badge> : <Badge tone="success">Decrease</Badge>
}

function PriceTable({ prices }: { prices: BillingPriceDto[] }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Price</TableHead>
            <TableHead>Effective</TableHead>
            <TableHead>Change</TableHead>
            <TableHead>Scheduled by</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prices.map((price) => (
            <TableRow key={price.id}>
              <TableCell className="tabular-nums">{formatPrice(price.priceMicros)}</TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">{new Date(price.effectiveAt).toLocaleString()}</TableCell>
              <TableCell>
                <ChangeBadge price={price} />
              </TableCell>
              <TableCell className="text-muted-foreground">{price.scheduledBy ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * The price per live envelope for every billed organization: now, scheduled,
 * and before. Any staff can read it; ADMIN schedules changes, which are
 * append-only.
 */
export function OpsPricingPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const query = usePriceSchedule()
  const [open, setOpen] = React.useState(false)

  function renderBody() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load the price schedule.')} />
    const schedule = query.data
    return (
      <div className="space-y-8">
        <Card>
          <CardContent className="space-y-1 pt-6">
            <p className="text-caption text-muted-foreground">Current price per live envelope</p>
            <p className="text-heading font-semibold tabular-nums">{formatPrice(schedule.current.priceMicros)}</p>
            <p className="text-caption text-muted-foreground">Since {new Date(schedule.current.effectiveAt).toLocaleString()}</p>
          </CardContent>
        </Card>
        <section className="space-y-3">
          <h2 className="text-title font-semibold">Scheduled</h2>
          {schedule.scheduled.length > 0 ? (
            <PriceTable prices={schedule.scheduled} />
          ) : (
            <EmptyState icon={CircleDollarSign} title="No changes scheduled" description="The current price stays in effect." />
          )}
        </section>
        {schedule.past.length > 0 ? (
          <section className="space-y-3">
            <h2 className="text-title font-semibold">Past</h2>
            <PriceTable prices={schedule.past} />
          </section>
        ) : null}
        {isAdmin ? <SchedulePriceDialog open={open} onOpenChange={setOpen} schedule={schedule} /> : null}
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="Pricing"
        description="The price per live envelope for every billed organization."
        actions={isAdmin && query.data ? <Button onClick={() => setOpen(true)}>Schedule a change</Button> : undefined}
      />
      {renderBody()}
    </>
  )
}
